/**
 * V2RollerSection — V2-native editor section for step.content.roller
 *
 * Edits: dataType, separator, wheels array (kind, list options / range, default value)
 * V2 paths: content.roller.*
 *
 * Replaces: OnboardingPickerSection (for V2 context)
 *
 * Sprint: schema-v2-migration, Sprint 4
 */

import React, { useState, useCallback, useMemo } from 'react';
import {
  Box,
  IconButton,
  Typography,
  Tooltip,
  useTheme,
  FormControl,
  Select,
  MenuItem,
  Collapse,
  Button,
  TextField,
} from '@mui/material';
import TuneIcon from '@mui/icons-material/Tune';
import type { SelectChangeEvent } from '@mui/material';
import {
  SAccordion,
  StyledTextField,
  BaseDialog,
  DialogHeader,
  DialogContent,
  DialogFooter,
  SortableList,
} from '@modules/ui-components';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { EditorGuideProvider } from '../../../contexts';
import { createV2RollerStyleSections } from '../../../config/v2RollerStyleConfig';
import { V2ListStyleSections } from './list/V2ListStyleSections';
import { SECTION_MARGIN_BOTTOM } from '../../../config/ui';
import { expandedToggleButtonSx, expandedToggleButtonActiveSx } from '../../../config/editorFieldStyles';
import {
  V2_DATA_TYPE_OPTIONS,
  V2_WHEEL_KIND_OPTIONS,
  MAX_WHEELS,
  MAX_WHEELS_INT,
  MAX_OPTIONS,
  createDefaultV2Wheel,
} from '../../../config/v2RollerConfig';
import { getValueByPath } from '../../../utils/v2FieldOperations';
import { getSectionTestId } from '../../../utils/testIdUtils';
import type { FlowStepNode } from '@modules/reactFlow/adapters/V2ScreenAdapter';

interface WheelOptionItem {
  value: string;
}

interface RollerWheel {
  kind: string;
  options: {
    localizedOptions?: Record<string, string[]>;
    from?: number;
    to?: number;
  };
  defaultValue?: Record<string, string>;
}

export interface V2RollerSectionProps {
  step: FlowStepNode;
  locale: string;
  onChange: (path: string, value: unknown) => void;
  disabled?: boolean;
  defaultExpanded?: boolean;
  /** Render function for embedded subsections (e.g. Variable Binding) */
  renderSubsections?: () => React.ReactNode;
  /** Badge count from subsections to aggregate with own badge */
  subsectionBadgeCount?: number;
  /** Where to render subsections relative to own content */
  subsectionPosition?: 'first' | 'last';
}

export const V2RollerSection: React.FC<V2RollerSectionProps> = ({
  step,
  locale,
  onChange,
  disabled = false,
  defaultExpanded = true,
  renderSubsections,
  subsectionBadgeCount,
  subsectionPosition,
}) => {
  const theme = useTheme();

  const dataType = useMemo(
    () => getValueByPath<string>(step, 'content.roller.dataType') || 'string',
    [step]
  );

  const wheels = useMemo(() => {
    const value = getValueByPath<RollerWheel[]>(step, 'content.roller.wheels');
    return Array.isArray(value) ? value : [];
  }, [step]);

  const separator = useMemo(
    () => getValueByPath<string>(step, 'content.roller.separator') || '',
    [step]
  );

  const effectiveMaxWheels = dataType === 'int' ? MAX_WHEELS_INT : MAX_WHEELS;

  const [showStyles, setShowStyles] = useState(false);
  const rollerStyleSections = useMemo(() => createV2RollerStyleSections(), []);

  const [expandedWheels, setExpandedWheels] = useState<Set<number>>(new Set([0]));
  const [deleteConfirm, setDeleteConfirm] = useState<{
    open: boolean;
    type: 'wheel' | 'option';
    wheelIndex: number;
    optionIndex?: number;
  }>({ open: false, type: 'wheel', wheelIndex: -1 });

  const toggleWheelExpanded = useCallback((index: number) => {
    setExpandedWheels((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  }, []);

  const handleDataTypeChange = useCallback(
    (event: SelectChangeEvent<string>) => {
      const newDataType = event.target.value;
      onChange('content.roller.dataType', newDataType);
      if (newDataType === 'int' && wheels.length > 1) {
        onChange('content.roller.wheels', [wheels[0]]);
      }
    },
    [onChange, wheels]
  );

  const handleSeparatorChange = useCallback(
    (value: string) => onChange('content.roller.separator', value),
    [onChange]
  );

  const handleAddWheel = useCallback(() => {
    if (wheels.length >= effectiveMaxWheels) return;
    const newWheels = [...wheels, createDefaultV2Wheel(locale)];
    onChange('content.roller.wheels', newWheels);
    setExpandedWheels((prev) => new Set([...prev, newWheels.length - 1]));
  }, [wheels, locale, onChange, effectiveMaxWheels]);

  const handleDeleteWheelClick = useCallback((wheelIndex: number) => {
    if (wheels.length <= 1) return;
    setDeleteConfirm({ open: true, type: 'wheel', wheelIndex });
  }, [wheels.length]);

  const handleDeleteWheelConfirm = useCallback(() => {
    const newWheels = wheels.filter((_, i) => i !== deleteConfirm.wheelIndex);
    onChange('content.roller.wheels', newWheels);
    setDeleteConfirm({ open: false, type: 'wheel', wheelIndex: -1 });
  }, [deleteConfirm, wheels, onChange]);

  const handleWheelKindChange = useCallback(
    (wheelIndex: number, event: SelectChangeEvent<string>) => {
      const newKind = event.target.value;
      if (newKind === 'range') {
        onChange(`content.roller.wheels.${wheelIndex}.options`, { from: 0, to: 10 });
      } else {
        onChange(`content.roller.wheels.${wheelIndex}.options`, {
          localizedOptions: { [locale]: ['Option 1', 'Option 2'] },
        });
      }
      onChange(`content.roller.wheels.${wheelIndex}.kind`, newKind);
      onChange(`content.roller.wheels.${wheelIndex}.defaultValue.${locale}`, '');
    },
    [locale, onChange]
  );

  const handleDefaultValueChange = useCallback(
    (wheelIndex: number, value: string) => {
      onChange(`content.roller.wheels.${wheelIndex}.defaultValue.${locale}`, value);
    },
    [locale, onChange]
  );

  const getWheelOptions = useCallback(
    (wheelIndex: number): string[] => {
      const value = getValueByPath<string[]>(
        step,
        `content.roller.wheels.${wheelIndex}.options.localizedOptions.${locale}`
      );
      return Array.isArray(value) ? value : [];
    },
    [step, locale]
  );

  const updateWheelOptions = useCallback(
    (wheelIndex: number, newOptions: string[]) => {
      onChange(`content.roller.wheels.${wheelIndex}.options.localizedOptions.${locale}`, newOptions);
    },
    [locale, onChange]
  );

  const handleAddOption = useCallback(
    (wheelIndex: number) => {
      const options = getWheelOptions(wheelIndex);
      if (options.length >= MAX_OPTIONS) return;
      updateWheelOptions(wheelIndex, [...options, '']);
    },
    [getWheelOptions, updateWheelOptions]
  );

  const handleDeleteOptionClick = useCallback(
    (wheelIndex: number, optionIndex: number) => {
      const options = getWheelOptions(wheelIndex);
      if (options.length <= 1) return;
      if (options.length === 2) {
        setDeleteConfirm({ open: true, type: 'option', wheelIndex, optionIndex });
      } else {
        updateWheelOptions(wheelIndex, options.filter((_, i) => i !== optionIndex));
      }
    },
    [getWheelOptions, updateWheelOptions]
  );

  const handleDeleteOptionConfirm = useCallback(() => {
    const { wheelIndex, optionIndex } = deleteConfirm;
    if (optionIndex === undefined) return;
    const options = getWheelOptions(wheelIndex);
    updateWheelOptions(wheelIndex, options.filter((_, i) => i !== optionIndex));
    setDeleteConfirm({ open: false, type: 'wheel', wheelIndex: -1 });
  }, [deleteConfirm, getWheelOptions, updateWheelOptions]);

  const handleOptionChange = useCallback(
    (wheelIndex: number, optionIndex: number, value: string) => {
      const options = getWheelOptions(wheelIndex);
      const newOptions = [...options];
      newOptions[optionIndex] = value;
      updateWheelOptions(wheelIndex, newOptions);
    },
    [getWheelOptions, updateWheelOptions]
  );

  const handleReorderOptions = useCallback(
    (wheelIndex: number, reorderedItems: WheelOptionItem[]) => {
      updateWheelOptions(wheelIndex, reorderedItems.map((item) => item.value));
    },
    [updateWheelOptions]
  );

  const renderListOptionsEditor = (wheelIndex: number) => {
    const options = getWheelOptions(wheelIndex);
    const optionItems: WheelOptionItem[] = options.map((value) => ({ value }));

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(1.5), mt: 2 }} data-testid={`roller-wheel-${wheelIndex}-options`}>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>OPTIONS</Typography>
        <SortableList
          items={optionItems}
          onReorder={(items: WheelOptionItem[]) => handleReorderOptions(wheelIndex, items)}
          disabled={disabled}
          gap="sm"
        >
          {(item: WheelOptionItem, index: number) => (
            <SortableList.Item>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, border: `1px solid ${theme.palette.divider}`, borderRadius: 1 }}>
                <Typography variant="caption" sx={{ color: 'text.secondary', minWidth: 24, textAlign: 'center' }}>
                  {index + 1}.
                </Typography>
                <StyledTextField
                  label=""
                  placeholder={`Option ${index + 1}`}
                  value={item.value || ''}
                  onChange={(value) => handleOptionChange(wheelIndex, index, value)}
                  disabled={disabled}
                  fullWidth
                  sx={{ flex: 1, minWidth: 0 }}
                />
                <Tooltip title={options.length <= 1 ? 'At least 1 option required' : 'Delete option'}>
                  <span>
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteOptionClick(wheelIndex, index)}
                      disabled={disabled || options.length <= 1}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            </SortableList.Item>
          )}
        </SortableList>
        <Tooltip title={options.length >= MAX_OPTIONS ? `Maximum ${MAX_OPTIONS} options` : 'Add new option'}>
          <span>
            <IconButton
              onClick={() => handleAddOption(wheelIndex)}
              disabled={disabled || options.length >= MAX_OPTIONS}
              sx={{ border: `1px dashed ${theme.palette.divider}`, borderRadius: 1, width: '100%', p: 1 }}
            >
              <AddIcon sx={{ mr: 0.5 }} fontSize="small" />
              <Typography variant="caption">Add Option ({options.length}/{MAX_OPTIONS})</Typography>
            </IconButton>
          </span>
        </Tooltip>
      </Box>
    );
  };

  const renderRangeOptionsEditor = (wheelIndex: number) => {
    const fromValue = getValueByPath<number>(step, `content.roller.wheels.${wheelIndex}.options.from`) ?? 0;
    const toValue = getValueByPath<number>(step, `content.roller.wheels.${wheelIndex}.options.to`) ?? 10;

    return (
      <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
        <Box sx={{ flex: 1 }} data-testid={`roller-wheel-${wheelIndex}-from`}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, mb: 0.5, display: 'block' }}>FROM</Typography>
          <TextField
            size="small"
            fullWidth
            value={String(fromValue)}
            onChange={(e) => {
              const v = parseInt(e.target.value, 10);
              if (!isNaN(v)) onChange(`content.roller.wheels.${wheelIndex}.options.from`, v);
            }}
            disabled={disabled}
            type="number"
          />
        </Box>
        <Box sx={{ flex: 1 }} data-testid={`roller-wheel-${wheelIndex}-to`}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, mb: 0.5, display: 'block' }}>TO</Typography>
          <TextField
            size="small"
            fullWidth
            value={String(toValue)}
            onChange={(e) => {
              const v = parseInt(e.target.value, 10);
              if (!isNaN(v)) onChange(`content.roller.wheels.${wheelIndex}.options.to`, v);
            }}
            disabled={disabled}
            type="number"
          />
        </Box>
      </Box>
    );
  };

  const renderDefaultValueEditor = (wheelIndex: number) => {
    const wheel = wheels[wheelIndex];
    if (!wheel) return null;
    const defaultValue = wheel.defaultValue?.[locale] || '';

    if (wheel.kind === 'range') {
      const from = getValueByPath<number>(step, `content.roller.wheels.${wheelIndex}.options.from`) ?? 0;
      const to = getValueByPath<number>(step, `content.roller.wheels.${wheelIndex}.options.to`) ?? 10;
      const rangeOptions: string[] = [];
      for (let i = from; i <= to; i++) rangeOptions.push(String(i));

      return (
        <Box sx={{ mt: 2 }} data-testid={`roller-wheel-${wheelIndex}-default`}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, mb: 0.5, display: 'block' }}>DEFAULT VALUE</Typography>
          <FormControl size="small" fullWidth>
            <Select value={defaultValue || String(from)} onChange={(e) => handleDefaultValueChange(wheelIndex, e.target.value)} disabled={disabled}>
              {rangeOptions.map((opt) => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
            </Select>
          </FormControl>
        </Box>
      );
    }

    const options = getWheelOptions(wheelIndex);
    return (
      <Box sx={{ mt: 2 }} data-testid={`roller-wheel-${wheelIndex}-default`}>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, mb: 0.5, display: 'block' }}>DEFAULT VALUE</Typography>
        <FormControl size="small" fullWidth>
          <Select value={defaultValue} onChange={(e) => handleDefaultValueChange(wheelIndex, e.target.value)} disabled={disabled} displayEmpty>
            <MenuItem value=""><em>None</em></MenuItem>
            {options.map((opt, idx) => <MenuItem key={idx} value={opt}>{opt || `Option ${idx + 1}`}</MenuItem>)}
          </Select>
        </FormControl>
      </Box>
    );
  };

  const renderWheelEditor = (wheel: RollerWheel, wheelIndex: number) => {
    const isWheelExpanded = expandedWheels.has(wheelIndex);

    return (
      <Box key={wheelIndex} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 1, mb: 1.5, overflow: 'hidden' }}>
        <Box
          sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.5, backgroundColor: theme.palette.action.hover, cursor: 'pointer' }}
          onClick={() => toggleWheelExpanded(wheelIndex)}
          data-testid={`roller-wheel-${wheelIndex}-header`}
        >
          <IconButton size="small" sx={{ p: 0 }}>
            {isWheelExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
          <Typography variant="body2" sx={{ fontWeight: 500, flex: 1 }}>WHEEL {wheelIndex + 1}</Typography>
          {wheels.length > 1 && (
            <Tooltip title="Delete wheel">
              <IconButton
                size="small"
                onClick={(e) => { e.stopPropagation(); handleDeleteWheelClick(wheelIndex); }}
                disabled={disabled}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
        <Collapse in={isWheelExpanded}>
          <Box sx={{ p: 2 }}>
            <FormControl size="small" fullWidth sx={{ mb: 2 }} data-testid={`roller-wheel-${wheelIndex}-kind`}>
              <Select
                value={wheel.kind || 'list'}
                onChange={(e) => handleWheelKindChange(wheelIndex, e)}
                disabled={disabled}
              >
                {V2_WHEEL_KIND_OPTIONS.map((opt) => <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>)}
              </Select>
            </FormControl>
            {renderDefaultValueEditor(wheelIndex)}
            {wheel.kind === 'range' ? renderRangeOptionsEditor(wheelIndex) : renderListOptionsEditor(wheelIndex)}
          </Box>
        </Collapse>
      </Box>
    );
  };

  return (
    <>
      <SAccordion
        title="Roller"
        collapsible
        defaultExpanded={defaultExpanded}
        badge={subsectionBadgeCount || undefined}
        badgeColor={subsectionBadgeCount ? 'warning' : undefined}
        sx={{ marginBottom: SECTION_MARGIN_BOTTOM }}
        data-testid={getSectionTestId('Roller')}
      >
        {subsectionPosition === 'first' && renderSubsections?.()}
        <EditorGuideProvider>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(2) }}>
            {/* Data Type Selector + Style Toggle (same row, matching V1 Picker) */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <FormControl size="small" sx={{ flex: 1 }} data-testid="roller-data-type-select">
                <Select value={dataType} onChange={handleDataTypeChange} disabled={disabled}>
                  {V2_DATA_TYPE_OPTIONS.map((opt) => <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>)}
                </Select>
              </FormControl>
              <IconButton
                onClick={() => setShowStyles((prev) => !prev)}
                disabled={disabled}
                size="small"
                data-testid="style-settings-button"
                sx={showStyles ? expandedToggleButtonActiveSx : expandedToggleButtonSx}
              >
                <TuneIcon fontSize="small" />
              </IconButton>
            </Box>

            {/* Separator */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }} data-testid="roller-separator-input">
              <Typography variant="body2" sx={{ fontWeight: 500, minWidth: 70 }}>Separator</Typography>
              <StyledTextField
                value={separator}
                onChange={handleSeparatorChange}
                placeholder="/"
                disabled={disabled}
                size="small"
                sx={{ flex: 1, maxWidth: 120 }}
              />
            </Box>

            {/* Style sections (collapsible) */}
            <Collapse in={showStyles} timeout="auto">
              <Box sx={{ pl: 1, mb: 2 }} data-testid="style-sections-container">
                <V2ListStyleSections
                  sections={rollerStyleSections}
                  currentItemType=""
                  step={step}
                  onChange={onChange}
                  disabled={disabled}
                />
              </Box>
            </Collapse>

            {/* Wheels */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }} data-testid="roller-wheels-header">
                <Typography variant="body2" sx={{ fontWeight: 500 }}>Wheels</Typography>
                <Tooltip title={wheels.length >= effectiveMaxWheels ? (dataType === 'int' ? 'Int type allows only 1 wheel' : `Maximum ${effectiveMaxWheels} wheels`) : 'Add wheel'}>
                  <span>
                    <Button size="small" startIcon={<AddIcon />} onClick={handleAddWheel} disabled={disabled || wheels.length >= effectiveMaxWheels} variant="outlined">
                      Add Wheel ({wheels.length}/{effectiveMaxWheels})
                    </Button>
                  </span>
                </Tooltip>
              </Box>
              {wheels.map((wheel, index) => renderWheelEditor(wheel, index))}
              {wheels.length === 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  No wheels configured. Click "Add Wheel" to create one.
                </Typography>
              )}
            </Box>
          </Box>
        </EditorGuideProvider>
        {subsectionPosition === 'last' && renderSubsections?.()}
      </SAccordion>

      {/* Delete Confirmation Dialog */}
      <BaseDialog open={deleteConfirm.open} onClose={() => setDeleteConfirm({ open: false, type: 'wheel', wheelIndex: -1 })} mode="modal" maxWidth="xs">
        <DialogHeader title={deleteConfirm.type === 'wheel' ? 'Delete Wheel' : 'Delete Option'} onClose={() => setDeleteConfirm({ open: false, type: 'wheel', wheelIndex: -1 })} />
        <DialogContent>
          <Typography>
            {deleteConfirm.type === 'wheel'
              ? 'Are you sure you want to delete this wheel? This action cannot be undone.'
              : 'Deleting this option will leave only 1 option in the list. Continue?'}
          </Typography>
        </DialogContent>
        <DialogFooter
          primaryAction={{
            label: 'Delete',
            onClick: deleteConfirm.type === 'wheel' ? handleDeleteWheelConfirm : handleDeleteOptionConfirm,
            variant: 'contained',
            color: 'error',
          }}
        />
      </BaseDialog>
    </>
  );
};
